<script setup>
import { watch } from 'vue'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { buildStudioExtensions } from './extensions/index.js'
import './studio.css'

// The Studio canvas. setStudioContext() must have been called (registry +
// media resolver) BEFORE this mounts — pill labels and image URLs resolve at
// render time. Recreated (:key) on conflict-reload so the undo stack never
// crosses an import boundary.
const { doc, editable = false, options = null } = defineProps({
  doc: { type: Object, required: true },
  editable: { type: Boolean, default: false },
  // buildStudioExtensions' options (the editing UI's picture uploader and
  // notice callback); read once, when the editor is created.
  options: { type: Object, default: null },
})

const emit = defineEmits(['update'])

const editor = useEditor({
  content: doc,
  editable,
  extensions: buildStudioExtensions(options ?? {}),
  // Lazy: getJSON() serializes the WHOLE document — on a 100-page template
  // that's a per-keystroke tax. Emit a getter; the store materializes it
  // when the autosave debounce actually fires.
  onUpdate: ({ editor: instance }) => emit('update', () => instance.getJSON()),
})

watch(
  () => editable,
  // emitUpdate: false — Tiptap's setEditable fires the update event by
  // default, which would mark the doc dirty and autosave a phantom draft
  // on every Muuda/Eelvaade switch (drafts then shadow future imports).
  (next) => editor.value?.setEditable(next, false),
)

defineExpose({ editor })
</script>

<template>
  <EditorContent :editor="editor" class="studio-editor" />
</template>
